import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight, TwoCol } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import { LessonPiano, Exercise } from "@/components/LessonPiano";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp, Anatomy,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

// The five-finger drill in C major — RH thumb on C, pinky on G.
// Each note one beat. C-D-E-F-G-F-E-D-C is 9 beats.
const FIVE_FINGER_RH: Exercise = {
  label: "C major five-finger drill · right hand",
  bpm: 92,
  totalBeats: 9,
  notes: [
    { pitch: "C4", time: 0, duration: 1, hand: "R" },
    { pitch: "D4", time: 1, duration: 1, hand: "R" },
    { pitch: "E4", time: 2, duration: 1, hand: "R" },
    { pitch: "F4", time: 3, duration: 1, hand: "R" },
    { pitch: "G4", time: 4, duration: 1, hand: "R" },
    { pitch: "F4", time: 5, duration: 1, hand: "R" },
    { pitch: "E4", time: 6, duration: 1, hand: "R" },
    { pitch: "D4", time: 7, duration: 1, hand: "R" },
    { pitch: "C4", time: 8, duration: 1, hand: "R" },
  ],
};

// Same drill plus LH C-octave grounding the pulse on every beat.
const FIVE_FINGER_BOTH: Exercise = {
  label: "Both hands · LH octave on every beat",
  bpm: 92,
  totalBeats: 9,
  notes: [
    ...FIVE_FINGER_RH.notes,
    // Left hand: C2+C3 octave thump on each beat.
    ...Array.from({ length: 9 }, (_, i) => ([
      { pitch: "C2", time: i, duration: 1, hand: "L" as const, velocity: 0.65 },
      { pitch: "C3", time: i, duration: 1, hand: "L" as const, velocity: 0.65 },
    ])).flat(),
  ],
};

// Minor version — flatten the 3rd to E♭.
const FIVE_FINGER_MINOR: Exercise = {
  label: "C minor variation · drop the 3rd to E♭",
  bpm: 92,
  totalBeats: 9,
  notes: [
    { pitch: "C4", time: 0, duration: 1, hand: "R" },
    { pitch: "D4", time: 1, duration: 1, hand: "R" },
    { pitch: "Eb4", time: 2, duration: 1, hand: "R" },
    { pitch: "F4", time: 3, duration: 1, hand: "R" },
    { pitch: "G4", time: 4, duration: 1, hand: "R" },
    { pitch: "F4", time: 5, duration: 1, hand: "R" },
    { pitch: "Eb4", time: 6, duration: 1, hand: "R" },
    { pitch: "D4", time: 7, duration: 1, hand: "R" },
    { pitch: "C4", time: 8, duration: 1, hand: "R" },
  ],
};

export default function PianoFoundation5() {
  const { lesson, course, accent } = findLesson("piano-foundation", "5");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Right hand sits on the C major scale: <strong>thumb on C, index on D, middle on E, ring on F, pinky on G</strong>. Play up and down with a steady pulse.</Highlight>
        <Highlight>Left hand grabs C with the pinky — and either holds the same C, or toggles between low C and the upper C for a richer sound.</Highlight>
        <Highlight>Sing as you play — note names get annoying; the <strong>swaras</strong> Sa, Re, Ga, Ma, Pa are easier on the tongue and lock the scale degrees in your ear.</Highlight>
        <Highlight>Convert major to minor by dropping the <strong>third</strong> (E) down a half-step to <strong>E♭</strong>. Same five-finger shape, completely different feeling.</Highlight>
        <Highlight>Once the right hand is solid, the left hand can <strong>"float"</strong> away from the home note — wander to F, G, A — and come back. That's how a finger drill becomes music.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The cornerstone" title="Five fingers, five notes, both hands" accent={accent.hex}>
        <p>
          The five-finger drill is the foundation of nearly every other piano exercise that
          comes after it — strength, dexterity, posture, timing, hand independence. The lesson opens with the right hand, since it's the one usually carrying the melody, and parks
          it on the C major scale.
        </p>

        <Anatomy title="The five fingers · numbered the same in both hands">
          <div className="flex flex-wrap items-center justify-center gap-3 text-[12px]">
            {[
              { n: 1, name: "Thumb" },
              { n: 2, name: "Index" },
              { n: 3, name: "Middle" },
              { n: 4, name: "Ring" },
              { n: 5, name: "Pinky" },
            ].map((f) => (
              <div key={f.n} className="flex w-20 flex-col items-center rounded-xl border border-amber-500/30 bg-amber-500/5 px-2 py-2">
                <div className="font-display text-2xl font-bold text-amber-300">{f.n}</div>
                <div className="text-nsm-dim">{f.name}</div>
              </div>
            ))}
          </div>
        </Anatomy>
      </Insight>

      <Insight eyebrow="The right-hand drill" title="C-D-E-F-G — and back down" accent={accent.hex}>
        <p>
          Park the right hand: thumb on C, index on D, middle on E, ring on F, pinky on G.
          Then play it up and back down with a steady pulse:
        </p>
        <p className="font-mono text-[14px] text-amber-300">
          C — D — E — F — G — F — E — D — C
        </p>

        <LessonPiano
          exercise={FIVE_FINGER_RH}
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "1" },
            { pitch: "D4", color: "#22c55e", label: "2" },
            { pitch: "E4", color: "#22c55e", label: "3" },
            { pitch: "F4", color: "#22c55e", label: "4" },
            { pitch: "G4", color: "#22c55e", label: "5" },
          ]}
          height={170}
        />

        <Tip>
          Press <strong>▶ Play</strong> to hear the drill at full tempo. Use the speed
          switcher to slow it down to <em>0.5×</em> while you learn the fingering — there's
          no problem with starting under tempo. The whole point is the steady pulse, not
          the speed.
        </Tip>

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="5"
          id="rh-five-finger"
          mode="sequence"
          title="Right-hand five-finger drill"
          prompt="Now you play it. Walk up and back down: C – D – E – F – G – F – E – D – C. The glowing key is your next note."
          notes={["C4", "D4", "E4", "F4", "G4", "F4", "E4", "D4", "C4"]}
          startOctave={3}
          octaves={2}
        />
      </Insight>

      <Insight eyebrow="Add the left hand" title="One C — or two — under every right-hand step" accent={accent.hex}>
        <p>
          The left hand keeps it simple. Take the note <strong>C</strong> — the root of the
          scale, the <em>Sa</em> if you're singing in swaras. Two options:
        </p>
        <ul className="space-y-1 text-[14px]">
          <li>
            <strong>One C</strong> — left-hand pinky on C, played on the pulse.
          </li>
          <li>
            <strong>Two Cs together</strong> — pinky on the lower C, thumb on the C above
            (an octave). Sounds richer.
          </li>
        </ul>
        <p>
          Then layer the hands: <strong>one left-hand strike on every pulse</strong>, which is
          one for each right-hand note — the LH holds time while the RH walks the scale. That
          is exactly what the ▶ demo above plays. Once it feels good, try
          <strong> toggling</strong> the LH between the lower C and the upper C — alternating
          on each pulse — for a small extra layer of movement. Then, if you want a busier
          floor under the scale, double the left hand to two strikes per right-hand note.
        </p>

        <KeyIdea>
          The left hand is holding time for both hands. Even when it's just two notes, the
          steady pulse it provides is what lets the right hand walk the scale without
          rushing.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Sing the scale" title="Use the swaras — they're easier on the tongue" accent={accent.hex}>
        <Pull>
          I generally prefer to go with the Indian swaras, which are the scale degrees
          which we say with very unique words which sound really nice on the tongue as
          well when you sing it.
        </Pull>

        <p>
          Sa, Re, Ga, Ma, Pa = scale degrees <strong>1, 2, 3, 4, 5</strong>. In C major: Sa
          is C, Re is D, Ga is E, Ma is F, Pa is G. The pleasure of swaras is that they're
          <em> portable</em> — the same syllables work in any key. Move to D major and Sa is
          now D; everything else shifts with it.
        </p>

        <Tip>
          Sing the swaras as the right hand plays them. If your hand is busy and your mouth is
          quiet, the drill is silently teaching only your fingers — not your ear.
        </Tip>
      </Insight>

      <Insight eyebrow="Switch hands" title="The left hand is a mirror" accent={accent.hex}>
        <p>
          When the right hand starts to tire, switch. The same drill on the left — but the
          mirror image: <strong>left pinky starts the job</strong> on C (one octave below),
          then ring → middle → index → thumb walks up the scale to G.
        </p>

        <TwoCol>
          <div>
            <div className="text-xs uppercase tracking-wider text-blue-300">Right hand</div>
            <p className="text-sm">Thumb (1) → Pinky (5). Plays up and down the scale.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-amber-300">Left hand</div>
            <p className="text-sm">Pinky (5) → Thumb (1). Same scale, mirrored fingering.</p>
          </div>
        </TwoCol>

        <p>
          Once the left hand is comfortable, put both hands together — playing in parallel up
          and down the scale. From there, you can speed up: two notes per pulse (eighth
          notes), or even four per pulse (sixteenth notes).
        </p>
      </Insight>

      <Insight eyebrow="Major to minor" title="Drop the third — same shape, sad sound" accent={accent.hex}>
        <p>
          The five-finger major drill becomes a minor drill with one tiny change: take the
          <strong> third note</strong> of the scale — that's <strong>E</strong> in C major —
          and move it down to the note immediately to its left: <strong>E♭</strong>.
        </p>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#3b82f6", label: "1" },
            { pitch: "D4", color: "#3b82f6", label: "2" },
            { pitch: "Eb4", color: "#ef4444", label: "♭3" },
            { pitch: "F4", color: "#3b82f6", label: "4" },
            { pitch: "G4", color: "#3b82f6", label: "5" },
          ]}
          height={170}
        />

        <Pull>
          Once I've done it in a major feeling, which sounds very positive and happy, I
          like that minor vibe as well, which sounds a bit more melancholic and a bit
          more introspective and a bit more serious.
        </Pull>
      </Insight>

      <Insight eyebrow="Float the left hand" title="Leave home, come back" accent={accent.hex}>
        <p>
          Once both hands are confident, the next step in the lesson is creative —
          let the left hand <em>float</em>. Don't pin it on C. Wander to F, G, A, then back to
          C. Sa is "home"; every other note is "leaving home". Going away and returning is
          how a drill turns into a story.
        </p>

        <Steps>
          <Step>Start anchored on C in the LH while the RH walks the scale.</Step>
          <Step>After 4 bars, move the LH to F. Same RH drill on top.</Step>
          <Step>4 more bars on G. Then A. Then back to C.</Step>
          <Step>
            You're now playing <strong>music</strong>. The chord under each LH note has
            quietly changed even though the RH never left the C major scale.
          </Step>
        </Steps>

        <Tip>
          Add <strong>dynamics</strong>. The piano lets you play softly or loudly — that's
          called dynamics. Walk softly while the LH is wandering away from home; come back
          loud when it lands on C again. Same notes, totally different feeling.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-6"
        label="Lesson 6 · Five Finger Drills (Part 2)"
        why="Three more drills next — thirds (alternate fingers), accents in groups of three, and both hands moving together. The five-finger position turns into actual technique."
      />
    </LessonShell>
  );
}
